/**
 * Entities the in-app assistant may query (request 0293 / platform spec 0012 §8.0 G5),
 * and the entity → module map used by access control (spec 0059 / request 0400).
 *
 * One row per module page that exposes a queryable entity (`entity` on
 * `PaginatedCrudPage` / `CrudGrid`). Gated at runtime by the user's module
 * instances (same source as the sidebar / `app.openModule`): curation says what
 * *may* be exposed; module access says what *this* user sees.
 *
 * `label` / `singularLabel` come from the EntityDescriptor via registry lookup
 * (`getDescriptorForRule`) at resolve time — do not import descriptors here
 * (would inflate the entry chunk that reaches AppLayout). `pluralLabel` and
 * `moduleSlug` stay manual (pt-BR plurals are irregular; there is no automatic
 * link between module directory slug and entity name).
 *
 * `readOnlyCapable` marks modules whose page uses `PaginatedCrudPage` /
 * `CatalogCrudPage` and therefore honor profile consultation-only grants.
 */
import { getDescriptorForRule } from '@syscolect/domain'

/** Manual catalog row — labels resolved from the descriptor at use time. */
export interface AssistantQueryableEntitySpec {
  /** Registry / descriptor name (`Product`, `SalesOrder`, …). */
  entity: string
  /** Plural noun for compact result messages (`12 pedidos`). */
  pluralLabel: string
  /**
   * Module slug as in `/m/:slug` — directory name with dashes removed
   * (`sales-order` → `salesorder`).
   */
  moduleSlug: string
  /**
   * Opt-in for `data.createRecord` (request 0295). Flat catalogs only — no
   * child collections, no stock side-effects. Service is loaded dynamically
   * via `assistantCreateServices.ts` (never imported here — entry chunk).
   */
  creatable?: true
  /**
   * Page uses `PaginatedCrudPage` / `CatalogCrudPage` and shows "Só consulta"
   * in the profile grant tree (spec 0059). Personal prefs are excluded.
   */
  readOnlyCapable?: true
}

export interface AssistantQueryableEntity extends AssistantQueryableEntitySpec {
  /** Label shown to the model (JSON Schema enum) and used in tool errors. */
  label: string
  /** Singular noun for compact result messages (`1 pedido`). */
  singularLabel: string
}

/**
 * Full catalog of module-backed entities (manual fields only). Filtered per
 * user via `filterQueryableEntities` — schema size tracks what the user can open.
 */
export const ASSISTANT_QUERYABLE_ENTITIES: readonly AssistantQueryableEntitySpec[] = [
  { entity: 'Brand', pluralLabel: 'marcas', moduleSlug: 'brand', creatable: true, readOnlyCapable: true },
  { entity: 'Color', pluralLabel: 'cores', moduleSlug: 'color', creatable: true, readOnlyCapable: true },
  { entity: 'Component', pluralLabel: 'componentes', moduleSlug: 'component', readOnlyCapable: true },
  { entity: 'CostCenter', pluralLabel: 'centros de custo', moduleSlug: 'costcenter', creatable: true, readOnlyCapable: true },
  {
    entity: 'DistributionCenter',
    pluralLabel: 'centros de distribuição',
    moduleSlug: 'distributioncenter',
    readOnlyCapable: true,
  },
  { entity: 'Factory', pluralLabel: 'fábricas', moduleSlug: 'factory', readOnlyCapable: true },
  { entity: 'HandlingUnit', pluralLabel: 'volumes', moduleSlug: 'handlingunit', readOnlyCapable: true },
  { entity: 'Invoice', pluralLabel: 'notas fiscais', moduleSlug: 'invoice', readOnlyCapable: true },
  { entity: 'Lot', pluralLabel: 'lotes', moduleSlug: 'lot', readOnlyCapable: true },
  { entity: 'Machine', pluralLabel: 'máquinas', moduleSlug: 'machine', readOnlyCapable: true },
  {
    entity: 'MachineGroup',
    pluralLabel: 'grupos de máquinas',
    moduleSlug: 'machinegroup',
    creatable: true,
    readOnlyCapable: true,
  },
  { entity: 'Operation', pluralLabel: 'operações', moduleSlug: 'operation', readOnlyCapable: true },
  { entity: 'Party', pluralLabel: 'parceiros', moduleSlug: 'party', readOnlyCapable: true },
  { entity: 'Process', pluralLabel: 'processos', moduleSlug: 'process', readOnlyCapable: true },
  { entity: 'Product', pluralLabel: 'produtos', moduleSlug: 'product', readOnlyCapable: true },
  {
    entity: 'ProductGroup',
    pluralLabel: 'grupos de produto',
    moduleSlug: 'productgroup',
    creatable: true,
    readOnlyCapable: true,
  },
  { entity: 'ProductUnit', pluralLabel: 'unidades do produto', moduleSlug: 'productunit', readOnlyCapable: true },
  {
    entity: 'ProductionEntry',
    pluralLabel: 'apontamentos de produção',
    moduleSlug: 'productionentry',
    readOnlyCapable: true,
  },
  {
    entity: 'ProductionOrder',
    pluralLabel: 'ordens de produção',
    moduleSlug: 'productionorder',
    readOnlyCapable: true,
  },
  {
    entity: 'ProductionRun',
    pluralLabel: 'execuções de produção',
    moduleSlug: 'productionrun',
    readOnlyCapable: true,
  },
  {
    entity: 'ProductionStop',
    pluralLabel: 'paradas de produção',
    moduleSlug: 'productionstop',
    readOnlyCapable: true,
  },
  {
    entity: 'ProductionStopReason',
    pluralLabel: 'motivos de parada',
    moduleSlug: 'productionstopreason',
    creatable: true,
    readOnlyCapable: true,
  },
  {
    entity: 'ProductionTicket',
    pluralLabel: 'talões de produção',
    moduleSlug: 'productionticket',
    readOnlyCapable: true,
  },
  { entity: 'PurchaseOrder', pluralLabel: 'ordens de compra', moduleSlug: 'purchaseorder', readOnlyCapable: true },
  { entity: 'SalesOrder', pluralLabel: 'pedidos', moduleSlug: 'salesorder', readOnlyCapable: true },
  // Personal preference list — not a business cadastro; no consultation-only grant UI.
  { entity: 'SavedSelection', pluralLabel: 'seleções salvas', moduleSlug: 'savedselection' },
  { entity: 'Sector', pluralLabel: 'setores', moduleSlug: 'sector', creatable: true, readOnlyCapable: true },
  { entity: 'SizeGrid', pluralLabel: 'grades de tamanhos', moduleSlug: 'sizegrid', readOnlyCapable: true },
  {
    entity: 'StandardPackaging',
    pluralLabel: 'embalagens padrão',
    moduleSlug: 'standardpackaging',
    readOnlyCapable: true,
  },
  { entity: 'StockLocation', pluralLabel: 'locais de estoque', moduleSlug: 'stocklocation', readOnlyCapable: true },
  {
    entity: 'StorageAddress',
    pluralLabel: 'endereços de armazenamento',
    moduleSlug: 'storageaddress',
    readOnlyCapable: true,
  },
  {
    entity: 'StorageAddressPattern',
    pluralLabel: 'padrões de endereço',
    moduleSlug: 'storageaddresspattern',
    readOnlyCapable: true,
  },
  { entity: 'TechnicalSheet', pluralLabel: 'fichas técnicas', moduleSlug: 'technicalsheet', readOnlyCapable: true },
  {
    entity: 'TechnicalSheetOverride',
    pluralLabel: 'overrides de ficha técnica',
    moduleSlug: 'technicalsheetoverride',
    readOnlyCapable: true,
  },
  {
    entity: 'TicketWorkspace',
    pluralLabel: 'espaços de tickets',
    moduleSlug: 'ticketworkspaces',
    readOnlyCapable: true,
  },
  { entity: 'Unit', pluralLabel: 'unidades de medida', moduleSlug: 'unit', creatable: true, readOnlyCapable: true },
  {
    entity: 'VariationOption',
    pluralLabel: 'opções de variação',
    moduleSlug: 'variationoption',
    readOnlyCapable: true,
  },
  { entity: 'WarehouseMap', pluralLabel: 'de-paras de depósito', moduleSlug: 'warehousemap', readOnlyCapable: true },
  { entity: 'WarehouseTask', pluralLabel: 'tarefas de armazém', moduleSlug: 'warehousetask', readOnlyCapable: true },
  {
    entity: 'WarehouseTaskType',
    pluralLabel: 'tipos de tarefa de armazém',
    moduleSlug: 'warehousetasktype',
    readOnlyCapable: true,
  },
  { entity: 'WorkCalendar', pluralLabel: 'calendários de trabalho', moduleSlug: 'workcalendar', readOnlyCapable: true },
]

/** Module slug for an entity, or undefined when the entity has no catalog row. */
export function entityModuleSlug(entity: string): string | undefined {
  return ASSISTANT_QUERYABLE_ENTITIES.find((e) => e.entity === entity)?.moduleSlug
}

/** Whether the profile grant tree shows "Só consulta" for this module slug. */
export function isReadOnlyCapableSlug(slug: string): boolean {
  const normalized = slug.toLowerCase()
  return ASSISTANT_QUERYABLE_ENTITIES.some(
    (e) => e.moduleSlug === normalized && e.readOnlyCapable === true,
  )
}

/**
 * Resolve singular/plural labels from the registry. Missing registration →
 * `undefined` (out of the tool enum); `getDescriptorForRule` already warns via
 * `rtLogService` and does not throw outside strict mode.
 */
export function resolveQueryableEntity(
  spec: AssistantQueryableEntitySpec,
): AssistantQueryableEntity | undefined {
  const descriptor = getDescriptorForRule(spec.entity, 'assistant-queryable')
  if (!descriptor) return undefined
  return {
    entity: spec.entity,
    label: descriptor.label,
    singularLabel: descriptor.label.toLocaleLowerCase('pt-BR'),
    pluralLabel: spec.pluralLabel,
    moduleSlug: spec.moduleSlug,
    ...(spec.creatable ? { creatable: true as const } : {}),
    ...(spec.readOnlyCapable ? { readOnlyCapable: true as const } : {}),
  }
}

/**
 * Entities the user can reach given their available module instances.
 * When every instance of a module is consultation-only, the entity stays
 * queryable but loses `creatable` (spec 0059 / request 0401).
 */
export function filterQueryableEntities(
  instances: readonly { slug: string; readOnly?: boolean }[],
): AssistantQueryableEntity[] {
  const bySlug = new Map<string, { any: boolean; anyFull: boolean }>()
  for (const instance of instances) {
    const cur = bySlug.get(instance.slug) ?? { any: false, anyFull: false }
    cur.any = true
    if (!instance.readOnly) cur.anyFull = true
    bySlug.set(instance.slug, cur)
  }
  const out: AssistantQueryableEntity[] = []
  for (const spec of ASSISTANT_QUERYABLE_ENTITIES) {
    const access = bySlug.get(spec.moduleSlug)
    if (!access?.any) continue
    const resolved = resolveQueryableEntity(spec)
    if (!resolved) continue
    if (resolved.creatable && !access.anyFull) {
      const { creatable: _dropped, ...rest } = resolved
      out.push(rest)
    } else {
      out.push(resolved)
    }
  }
  return out
}

/** Creatable subset the user can reach (same module-access gate as queryable). */
export function filterCreatableEntities(
  instances: readonly { slug: string; readOnly?: boolean }[],
): AssistantQueryableEntity[] {
  return filterQueryableEntities(instances).filter((e) => e.creatable === true)
}

/**
 * Stable fingerprint of the queryable subset (labels + creatable, sorted).
 * Must change when consultation-only strips `creatable` so global tools refresh
 * and drop `data.createRecord` (request 0401).
 */
export function queryableCatalogKey(
  instances: readonly { slug: string; readOnly?: boolean }[],
): string {
  return filterQueryableEntities(instances)
    .map((e) => `${e.label}${e.creatable ? '+' : ''}`)
    .sort()
    .join('\n')
}
